import * as React from 'react'
import {ReactElement} from 'react'
import {
    NatResidentLoginInfoSessionStorageHelper,
    ResidentLoginInfoSessionStorageKey,
} from '../../../common/authentication/NatResidentLoginInfoSessionStorageHelper'
import {NatApplicationRepo} from '../repository/NatApplicationRepo'
import NatCurrentDateTimeRepo from '../../currentDateTime/repository/NatCurrentDateTimeRepo'
import NatCondominiumRepo from '../../condominium/repository/NatCondominiumRepo'
import {PlainBackLink} from '../../../../styledComponents/Links'
import {BlockSpacer} from '../../../../styledComponents/ResponsiveConvenience'
import {DisplayItemDividerForPageTop} from '../../../../styledComponents/DisplayItem'
import {Box} from '@mui/material'
import {Nullable} from '../../../../app/TypeHelpers'
import {NatResidentParkingUsesWithdrawalLocationState} from './NatResidentApplicationInterface'
import HolidaysRepository from '../../../../components/HolidaysRepository'
import {PageTitle} from '../common/resident/NatResidentApplicationsCommon'
import {
    createResidentWithErrorHandlingPage,
    ResidentWithErrorHandlingPagePropsSet,
} from '../../../../components/shared/hoc/createResidentWithErrorHandlingPage'
import {
    HeadingAreaMessageComponentForResident,
} from '../../../../components/shared/pageBaseComponents/HeadingAreaMessageComponentForResident'
import {Messages} from '../../../../Messages'
import {
    NatPutParkingUsesWithdrawalRequest,
    NatPutParkingUsesWithdrawalResponse,
} from '../repository/NatPutParkingUsesWithdrawal'
import NatResidentParkingUsesWithdrawalForm from './resident/NatResidentParkingUsesWithdrawalForm'
import {Location} from 'react-router-dom'

interface Props {
    condominiumCd: string
    applyId?: string
    applicationRepo: NatApplicationRepo
    condominiumRepo: NatCondominiumRepo
    currentDateTimeRepo: NatCurrentDateTimeRepo
    holidaysRepo: HolidaysRepository
    location: Location<NatResidentParkingUsesWithdrawalLocationState>
}

interface State {
    applicantFamilyName: Nullable<string>
    applicantFirstName: Nullable<string>
    withdrawalArea: Nullable<string>
    withdrawalDate: Nullable<string>
    carCarryingOutDate: Nullable<string>
    isLoadCompleted: boolean
}

class NatResidentParkingUsesWithdrawalInputPage
    extends React.Component<Props & ResidentWithErrorHandlingPagePropsSet, State> {

    constructor(props: Props & ResidentWithErrorHandlingPagePropsSet) {
        super(props)

        this.state = {
            applicantFamilyName: null,
            applicantFirstName: null,
            withdrawalArea: null,
            withdrawalDate: null,
            carCarryingOutDate: null,
            isLoadCompleted: false,
        }

        this.onClickApplyConfirmButton = this.onClickApplyConfirmButton.bind(this)
    }

    async componentDidMount(): Promise<void> {
        const {applyId, condominiumCd, applicationRepo, eventEmitter} = this.props
        const applyInfo = this.props.location.state?.applyInfo

        const loginFamilyName = NatResidentLoginInfoSessionStorageHelper
            .getStringItemOrNull(ResidentLoginInfoSessionStorageKey.LOGIN_FAMILY_NAME)
        const loginFirstName = NatResidentLoginInfoSessionStorageHelper
            .getStringItemOrNull(ResidentLoginInfoSessionStorageKey.LOGIN_FIRST_NAME)

        const defaultApplicantFamilyName = applyInfo?.applicantFamilyName ?? loginFamilyName
        const defaultApplicantFirstName = applyInfo?.applicantFirstName ?? loginFirstName
        let defaultWithdrawalArea = applyInfo?.withdrawalArea ?? null
        let defaultWithdrawalDate = applyInfo?.withdrawalDate ?? null
        let defaultCarCarryingOutDate = applyInfo?.carCarryingOutDate ?? null

        if (!applyInfo && applyId) {
            await this.props.eventEmitter.showLoading()
            try {
                const parkingUsesWithdrawalDetail = await this.props.asyncCallFunction(
                    applicationRepo.getParkingUsesWithdrawalForResident.bind(applicationRepo, condominiumCd, applyId),
                    (httpErrorOrNetworkError) => {
                        eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                    },
                )
                if (parkingUsesWithdrawalDetail && parkingUsesWithdrawalDetail.details) {
                    defaultWithdrawalArea = parkingUsesWithdrawalDetail.details.withdrawalArea
                    defaultWithdrawalDate = parkingUsesWithdrawalDetail.details.withdrawalDate
                    defaultCarCarryingOutDate = parkingUsesWithdrawalDetail.details.carCarryingOutDate
                }
            } finally {
                this.props.eventEmitter.closeLoading()
            }
        }

        this.setState({
            applicantFamilyName: defaultApplicantFamilyName,
            applicantFirstName: defaultApplicantFirstName,
            withdrawalArea: defaultWithdrawalArea,
            withdrawalDate: defaultWithdrawalDate,
            carCarryingOutDate: defaultCarCarryingOutDate,
            isLoadCompleted: true,
        })
    }

    async onClickApplyConfirmButton(request: NatPutParkingUsesWithdrawalRequest): Promise<void> {
        const {applicationRepo, condominiumCd} = this.props

        await this.props.eventEmitter.showLoading()
        try {
            const response: Nullable<NatPutParkingUsesWithdrawalResponse> = await this.props.asyncCallFunction(
                applicationRepo.applyParkingUsesWithdrawal.bind(
                    applicationRepo, condominiumCd, request),
                async (httpErrorOrNetworkError) => {
                    await httpErrorOrNetworkError.broadcast(this.props.eventEmitter)
                },
                {
                    defaultErrorTitle: Messages.EOPCR0014.toString(),
                    defaultErrorTitlePriority: true,
                    replacementDetailErrorMessages: [{
                        targetMessageId: Messages.EOPCA0004,
                        replacementMessageId: Messages.EOPCR0015,
                    }],
                },
            )

            if (response && response.requestError === null) {
                const locationState: NatResidentParkingUsesWithdrawalLocationState = {
                    applyInfo: {
                        applicantFamilyName: request.applicantFamilyName,
                        applicantFirstName: request.applicantFirstName,
                        withdrawalArea: request.withdrawalArea,
                        withdrawalDate: request.withdrawalDate,
                        carCarryingOutDate: request.carCarryingOutDate,
                    },
                    applyId: this.props.applyId,
                }

                this.props.navigate(`/resident/condominiums/${this.props.condominiumCd}/applications/parkingUsesWithdrawal/confirm`,
                    {state: JSON.parse(JSON.stringify(locationState))},
                )
            }
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    render(): ReactElement {
        if (!this.state.isLoadCompleted) {
            return (<></>)
        }

        return (
            <>
                <PlainBackLink
                    to={this.props.applyId
                        ? `/resident/condominiums/${this.props.condominiumCd}/applications/parkingUsesWithdrawal/${this.props.applyId}`
                        : `/resident/condominiums/${this.props.condominiumCd}/applications`}>
                    戻る
                </PlainBackLink>
                <BlockSpacer height={'16px'}/>
                <DisplayItemDividerForPageTop/>
                <BlockSpacer height={'30px'}/>
                <PageTitle>
                    駐車場使用契約解約届
                </PageTitle>
                <Box height={'20px'}/>
                <Box>
                    駐車場の使用を中止したい場合に提出が必要です。使用規則に定められた期限までに必ず提出してください。
                </Box>
                <Box height={'20px'}/>
                <HeadingAreaMessageComponentForResident eventEmitter={this.props.eventEmitter}/>
                <NatResidentParkingUsesWithdrawalForm
                    {...this.props}
                    {...this.state}
                    submitButtonLabel={'申請内容を確認する'}
                    onClickApplyConfirmButton={this.onClickApplyConfirmButton}
                />
            </>
        )
    }
}

export default createResidentWithErrorHandlingPage(NatResidentParkingUsesWithdrawalInputPage)
